import React, { useRef, useState } from 'react';
import { Box, chakra, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { useClickOutside } from '@/hooks/useClickOutside';
import { DropdownMenu, DropdownOption, DropdownTrigger } from '@/components/common/dropdown';
import {
	ASSIGNABLE_ROLE_OPTIONS,
	type AssignableRole,
	DEFAULT_ASSIGNABLE_ROLE,
	type RoleDefinition,
} from '@/utils/roleUtils';

export type AccessScope = AssignableRole;

interface AccessScopeSelectorProps {
	value: AccessScope;
	onChange: (value: AccessScope) => void;
	options?: readonly RoleDefinition<AssignableRole>[];
}

const AccessScopeSelector: React.FC<AccessScopeSelectorProps> = ({ value, onChange, options }) => {
	const dropdownRef = useRef<HTMLDivElement>(null);
	const [isOpen, setIsOpen] = useState(false);
	useClickOutside(dropdownRef, () => setIsOpen(false));

	const resolvedOptions = options ?? ASSIGNABLE_ROLE_OPTIONS;
	const selectedOption: RoleDefinition =
		resolvedOptions.find((opt) => opt.value === value) ?? DEFAULT_ASSIGNABLE_ROLE;

	const handleSelect = (nextValue: AccessScope) => {
		onChange(nextValue);
		setIsOpen(false);
	};

	return (
		<HStack gap={3} align="center" justify="space-between" w="full">
			<Text textStyle="label" color="fg.muted" flexShrink={0}>
				Access level
			</Text>

			<Box position="relative" flexShrink={0} ml="auto" ref={dropdownRef}>
				<DropdownTrigger isOpen={isOpen} onToggle={() => setIsOpen((current) => !current)}>
					<HStack gap={2}>
						<Icon as={selectedOption.icon} boxSize="14px" color="fg.muted" />
						<Text as="span">{selectedOption.label}</Text>
					</HStack>
				</DropdownTrigger>

				<DropdownMenu isOpen={isOpen} minW="260px" maxH="320px">
					{resolvedOptions.map((option) => {
						const isSelected = value === option.value;
						return (
							<DropdownOption
								key={option.value}
								selected={isSelected}
								onClick={() => handleSelect(option.value)}
							>
								<HStack gap={3} align="center">
									<Icon as={option.icon} boxSize="14px" color="fg.muted" />
									<VStack gap={0.5} align="start">
										<Text as="span" fontWeight={isSelected ? 600 : 500}>
											{option.label}
										</Text>
										<Text as="span" fontSize="xs" color="fg.muted" lineHeight={1.35}>
											{option.description}
										</Text>
									</VStack>
								</HStack>
							</DropdownOption>
						);
					})}

					<Box borderTop="1px solid" borderColor="border.default" mx={3} my={1} />
					{/* pb is short because DropdownMenu's content already pads the panel's bottom edge. */}
					<Text fontSize="xs" color="fg.muted" lineHeight={1.4} px={4} pt={2} pb={1}>
						Access level sets what one can <chakra.span fontStyle="italic">do</chakra.span>. What can be{' '}
						<chakra.span fontStyle="italic">seen</chakra.span> is set per record.
					</Text>
				</DropdownMenu>
			</Box>
		</HStack>
	);
};

export default AccessScopeSelector;
